/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { createContext, ReactNode, useContext, useEffect, useId, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Box } from "@mui/material";

const CarouselContext = createContext<{
  carousel: ReturnType<typeof useEmblaCarousel> | null;
  id: string | null;
}>({
  carousel: null,
  id: null,
});

export const useCarousel = () => {
  const { carousel, ...rest } = useContext(CarouselContext);

  return {
    ...rest,
    carouselRef: carousel?.[0],
    carouselApi: carousel?.[1],
  };
};

export type CarouselProviderProps = { children: ReactNode; label?: string; watchDragNonTouch?: boolean } & Parameters<typeof useEmblaCarousel>[0];

export const CarouselProvider = ({ children, label, watchDragNonTouch = false, ...options }: CarouselProviderProps) => {
  const id = useId();
  const allSlidesInView = useRef(false);

  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  const embla = useEmblaCarousel({
    align: "start",
    inViewThreshold: 0.5,
    ...options,
    watchDrag: (emblaApi, event) => {
      if (allSlidesInView.current || (!watchDragNonTouch && !isTouchDevice)) {
        return false;
      }

      if (typeof options.watchDrag === "function") {
        return options.watchDrag(emblaApi, event);
      }

      return options.watchDrag ?? true;
    },
  });

  useEffect(() => {
    const emblaApi = embla?.[1];

    if (!emblaApi) {
      return;
    }

    const handleChange = (api: typeof emblaApi) => {
      allSlidesInView.current = api.scrollSnapList().length <= 1;
    };

    emblaApi.on("init", handleChange);
    emblaApi.on("reInit", handleChange);
    emblaApi.on("resize", handleChange);
    emblaApi.on("slidesInView", handleChange);

    return () => {
      emblaApi.off("init", handleChange);
      emblaApi.off("reInit", handleChange);
      emblaApi.off("resize", handleChange);
      emblaApi.off("slidesInView", handleChange);
    };
  }, [embla]);

  return (
    <CarouselContext.Provider value={{ carousel: embla, id }}>
      <Box role="region" aria-roledescription="carousel" aria-label={label}>
        {children}
      </Box>
    </CarouselContext.Provider>
  );
};
